
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Users, Plus, Search, Filter, RefreshCw, AlertTriangle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminMissionParticipants } from '@/hooks/useAdminMissionParticipants';
import { useMissionUserManagement } from '@/hooks/useMissionUserManagement';
import MissionParticipantCard from './MissionParticipantCard';
import AddUserToMissionModal from './AddUserToMissionModal';
import { debugLogger } from '@/utils/debugLogger';

const MissionParticipantsList = () => {
  const navigate = useNavigate();
  const { missionId } = useParams<{ missionId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  debugLogger.debug('MissionParticipantsList mounted with missionId:', missionId);

  // Validate missionId
  if (!missionId) {
    debugLogger.error('Mission ID is missing from URL params');
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardContent className="p-8">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-2" />
              <h3 className="text-white text-lg font-semibold mb-2">Erro: ID da Missão Não Encontrado</h3>
              <p className="text-white/60 text-sm mb-4">
                Não foi possível identificar a missão. Verifique o URL.
              </p>
              <Button
                variant="admin-primary"
                onClick={() => navigate('/main/joia/admin/missions')}
              >
                Voltar para Missões
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { participants, loading, error, refetch } = useAdminMissionParticipants(missionId);
  const { addUser, removeUser, isAddingUser, isRemovingUser } = useMissionUserManagement(missionId);

  const filteredParticipants = participants.filter((participant) => {
    const matchesSearch = participant.user.username
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || participant.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleAddUser = (userId: string) => {
    addUser(userId);
    setShowAddModal(false);
  };

  const handleForceRefresh = () => {
    debugLogger.debug('Force refreshing participants data...');
    refetch();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-6">
          {/* Loading Header */}
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-8 w-64" />
          </div>
          
          {/* Loading Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-black/20 backdrop-blur-sm border-white/10">
                <CardContent className="p-6">
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    debugLogger.error('Error in MissionParticipantsList:', error);
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardContent className="p-8">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">Erro ao Carregar Participantes</h3>
              <p className="text-white/60 text-sm mb-2">
                {error.message || 'Ocorreu um erro inesperado'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="admin-primary"
                  onClick={handleForceRefresh}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tentar Novamente
                </Button>
                <Button
                  variant="admin-outline"
                  onClick={() => navigate('/main/joia/admin/missions')}
                >
                  Voltar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="admin-outline"
              onClick={() => navigate('/main/joia/admin/missions')}
              className="flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Participantes da Missão
              </h1>
              <p className="text-white/60 text-sm">
                {participants.length} participante{participants.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="admin-outline"
              onClick={handleForceRefresh}
              className="flex-shrink-0"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
            <Button
              variant="admin-primary"
              onClick={() => setShowAddModal(true)}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Usuário
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                <Input
                  placeholder="Buscar por username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                />
              </div>

              {/* Status Filter */}
              <div className="w-full sm:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-black/95 border-white/20 text-white">
                    <SelectItem value="all">Todos os Status</SelectItem>
                    <SelectItem value="ongoing">Em Andamento</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                    <SelectItem value="expired">Expirada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participants Grid */}
        {filteredParticipants.length === 0 ? (
          <Card className="bg-black/20 backdrop-blur-sm border-white/10">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">
                  {searchTerm || statusFilter !== 'all' ? 'Nenhum participante encontrado' : 'Nenhum participante'}
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Tente ajustar os filtros de busca'
                    : 'Esta missão ainda não possui participantes.'
                  }
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="admin-primary"
                    onClick={handleForceRefresh}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Recarregar Dados
                  </Button>
                  {!searchTerm && statusFilter === 'all' && (
                    <Button
                      variant="admin-outline"
                      onClick={() => setShowAddModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Usuário
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredParticipants.map((participant) => (
              <MissionParticipantCard
                key={participant.id}
                participant={participant}
                onRemove={removeUser}
                isRemoving={isRemovingUser}
              />
            ))}
          </div>
        )}

        {/* Stats Summary */}
        <Card className="bg-black/20 backdrop-blur-sm border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-400" />
              Resumo dos Participantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {participants.length}
                </div>
                <div className="text-white/60 text-sm">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {participants.filter(p => p.status === 'ongoing').length}
                </div>
                <div className="text-white/60 text-sm">Em Andamento</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {participants.filter(p => p.status === 'completed').length}
                </div>
                <div className="text-white/60 text-sm">Concluída</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">
                  {participants.filter(p => p.status === 'expired').length}
                </div>
                <div className="text-white/60 text-sm">Expirada</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add User Modal */}
      <AddUserToMissionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddUser={handleAddUser}
        isAdding={isAddingUser}
        missionId={missionId}
      />
    </div>
  );
};

export default MissionParticipantsList;
